import type { ExtractionHint } from "@noa/api-core";

export const SAMPLE_PATHS = [
  "/0000-Batiments/Procedures/PROC-001.pdf",
  "/1000-Wabush/Maintenance/Rev.3/Manual-2024.pdf",
  "/2000-Bloom/Safety/UT-050.pdf",
  "/3000-Ferroviaire/Engineering/DWG-ABC-001.pdf",
  "/2000-Bloom/04 Plans/E/2000-CV-030A-100-001-R02.pdf",
];

export const SAMPLE_FILENAMES = [
  "PROC-001.pdf",
  "UT-12345-R02.pdf",
  "2000-CV-030A-100-001-R02.pdf",
  "Fiche-Espace-Clos.docx",
];

export interface PatternTestResult {
  success: boolean;
  captured: string | null;
  error?: string;
}

export type ExtractionSource = "static" | "metadata" | "path" | "computed" | "llm";

export function inferExtractionSource(extraction?: ExtractionHint): ExtractionSource {
  if (!extraction) return "static";
  if (extraction.from_field && extraction.transform) return "computed";
  if (extraction.from_llm) return "llm";
  if (extraction.from_metadata_key) return "metadata";
  if (extraction.from_path_pattern || extraction.from_filename_pattern || extraction.from_path_prefix) return "path";
  return "static";
}

export function getFieldsToKeepOnSourceChange(
  newSource: ExtractionSource,
  currentExtraction: ExtractionHint | undefined
): Partial<ExtractionHint> {
  if (!currentExtraction) return {};

  const fieldsToKeep: Partial<ExtractionHint> = {};

  switch (newSource) {
    case "static":
      if (currentExtraction.default_value !== undefined) {
        fieldsToKeep.default_value = currentExtraction.default_value;
      }
      break;
    case "metadata":
      if (currentExtraction.from_metadata_key) fieldsToKeep.from_metadata_key = currentExtraction.from_metadata_key;
      if (currentExtraction.default_value !== undefined) fieldsToKeep.default_value = currentExtraction.default_value;
      break;
    case "path":
      if (currentExtraction.from_path_pattern) fieldsToKeep.from_path_pattern = currentExtraction.from_path_pattern;
      if (currentExtraction.from_filename_pattern)
        fieldsToKeep.from_filename_pattern = currentExtraction.from_filename_pattern;
      if (currentExtraction.from_path_prefix) fieldsToKeep.from_path_prefix = currentExtraction.from_path_prefix;
      if (currentExtraction.regex_flags) fieldsToKeep.regex_flags = currentExtraction.regex_flags;
      if (currentExtraction.value_map) fieldsToKeep.value_map = currentExtraction.value_map;
      if (currentExtraction.default_value !== undefined) fieldsToKeep.default_value = currentExtraction.default_value;
      break;
    case "computed":
      if (currentExtraction.from_field) fieldsToKeep.from_field = currentExtraction.from_field;
      if (currentExtraction.transform) fieldsToKeep.transform = currentExtraction.transform;
      break;
    case "llm":
      if (currentExtraction.from_llm) fieldsToKeep.from_llm = currentExtraction.from_llm;
      break;
  }

  return fieldsToKeep;
}

export function extractFilename(path: string): string {
  const parts = path.split("/");
  return parts[parts.length - 1] || "";
}

/** Python re flags supported by the schema backend, mapped to JS RegExp flags. */
const PY_FLAG_TO_JS: Record<string, string> = {
  IGNORECASE: "i",
  MULTILINE: "m",
  DOTALL: "s",
};

/**
 * Build a JS RegExp mirroring the server-side Python re.search semantics.
 *
 * - Applies the schema's `regex_flags` (IGNORECASE/MULTILINE/DOTALL), which the
 *   previous implementation ignored — a case-insensitive pattern showed
 *   "no match" in the tester while matching on the server.
 * - Translates Python named groups `(?P<name>...)` / `(?P=name)` to their JS
 *   equivalents so server-valid patterns don't get flagged invalid here.
 */
export function buildJsRegex(pattern: string, flags?: string[] | null): RegExp {
  const jsFlags = (flags ?? []).map((f) => PY_FLAG_TO_JS[f.toUpperCase()] ?? "").join("");
  const jsPattern = pattern.replaceAll("(?P<", "(?<").replaceAll(/\(\?P=(\w+)\)/g, "\\k<$1>");
  return new RegExp(jsPattern, jsFlags);
}

function capturedValue(match: RegExpMatchArray): string {
  // First capture group when present — even if it captured an empty string
  return match[1] !== undefined ? match[1] : match[0];
}

export function testPattern(pattern: string | undefined, input: string, flags?: string[] | null): PatternTestResult {
  if (!pattern) return { success: false, captured: null };

  try {
    // Patterns are raw regexes evaluated server-side with Python's re.search;
    // slashes are literal characters (path separators), never delimiters
    const regex = buildJsRegex(pattern, flags);

    const match = input.match(regex);
    if (match) {
      return { success: true, captured: capturedValue(match) };
    }
    return { success: false, captured: null };
  } catch (e) {
    return { success: false, captured: null, error: (e as Error).message };
  }
}

export interface PatternValidationResult {
  isValid: boolean;
  matches: string[];
  error: string | null;
}

export function validatePatternWithSamples(
  pattern: string | undefined,
  samples: string[],
  flags?: string[] | null
): PatternValidationResult {
  if (!pattern) return { isValid: true, matches: [], error: null };

  try {
    const regex = buildJsRegex(pattern, flags);
    const matches: string[] = [];

    for (const sample of samples) {
      const match = sample.match(regex);
      if (match) {
        matches.push(capturedValue(match));
      }
    }

    return { isValid: true, matches, error: null };
  } catch (e) {
    return { isValid: false, matches: [], error: (e as Error).message };
  }
}
